<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    width="848"
    role="dialog"
    aria-modal="true"
    aria-labelledby="gg-drawer-additems-title"
    class="gg-idrawer"
    @input="$emit('input', $event)"
  >
    <div class="gg-idrawer__header">
      <h2 id="gg-drawer-additems-title" class="gg-idrawer__title">Adicionar template ou cena</h2>
      <button type="button" class="gg-idrawer__close" aria-label="Fechar" @click="close">
        <v-icon size="24">mdi-close</v-icon>
      </button>
    </div>

    <div class="gg-idrawer__body">
      <div class="gg-idrawer__search">
        <input
          v-model="search"
          type="text"
          class="gg-idrawer__search-input"
          placeholder="Digite para buscar templates ou cenas"
          aria-label="Buscar templates ou cenas"
        />
        <span class="gg-idrawer__search-icon" aria-hidden="true">
          <v-icon size="20">mdi-magnify</v-icon>
        </span>
      </div>

      <div class="gg-idrawer__counter">
        <span class="gg-idrawer__count">
          {{ filtered.length }} disponíve{{ filtered.length === 1 ? 'l' : 'is' }}
        </span>
        <span class="gg-idrawer__selected">
          <span class="gg-idrawer__count">
            {{ selected.length }} selecionado{{ selected.length === 1 ? '' : 's' }}
          </span>
          <button
            v-if="selected.length"
            type="button"
            class="gg-idrawer__clear"
            aria-label="Limpar seleção"
            @click="selected = []"
          >
            <v-icon size="20">mdi-close-circle</v-icon>
          </button>
        </span>
      </div>

      <div class="gg-idrawer__grid">
        <label
          v-for="scene in filtered"
          :key="scene.id_gh_cena"
          class="gg-idrawer__item"
          :class="{
            'gg-idrawer__item--selected': isSelected(scene),
            'gg-add-item--in-playlist': isInPlaylist(scene),
          }"
          :title="isInPlaylist(scene) ? 'Já está nesta playlist' : scene.dsc_nome_cena"
        >
          <input
            type="checkbox"
            class="gg-idrawer__control"
            :checked="isSelected(scene)"
            :disabled="isInPlaylist(scene)"
            @change="toggle(scene)"
          />
          <span class="gg-idrawer__thumb">
            <img
              v-if="thumbUrl(scene) && !thumbError[scene.id_gh_cena]"
              :src="thumbUrl(scene)"
              :alt="scene.dsc_nome_cena"
              @error="onThumbError(scene.id_gh_cena)"
            />
            <v-icon v-else size="32" class="gg-add-item__fallback">mdi-cube-outline</v-icon>
          </span>
          <span class="gg-idrawer__label">{{ scene.dsc_nome_cena || 'Cena sem nome' }}</span>
        </label>
      </div>

      <div v-if="!filtered.length" class="sc-empty-state">
        <span class="sc-empty-state__title">
          {{ search
            ? 'Nenhum template ou cena para a busca.'
            : 'Nenhum template ou cena importado nesta produção.' }}
        </span>
      </div>
    </div>

    <div class="gg-idrawer__footer">
      <button type="button" class="gg-idrawer__submit" :disabled="!canAdd" @click="doAdd">
        {{ adding ? 'Adicionando…' : 'Adicionar' }}
      </button>
    </div>
  </v-navigation-drawer>
</template>

<script>
/**
 * Adds items to an existing playlist from what the production already has —
 * the templates and cenas imported into it, i.e. the same list the
 * "Templates e cenas" panel shows.
 *
 * This is NOT the Vizrt catalog. The catalog is how a template enters the
 * production ("Importar templates e cenas"); this drawer only arranges what is
 * already there, so nothing here creates a cena.
 */
import { mapActions, mapGetters } from 'vuex'
import { getVizrtThumbnailUrl } from '@/store/modules/globographics'

import ggDrawerA11y from '@/mixins/ggDrawerA11y'

export default {
  mixins: [ggDrawerA11y],
  name: 'GloboGraphicsAddItemsDrawer',
  props: {
    value: { type: Boolean, default: false },
    idPlaylist: { type: [Number, String], default: null },
    codProducao: { type: [Number, String], default: null },
    // Cenas already on this playlist — shown but not selectable.
    existingIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      search: '',
      selected: [],
      thumbError: {},
      adding: false,
    }
  },
  computed: {
    ...mapGetters({ savedScenes: 'globographics/getterSavedScenes' }),
    filtered() {
      const list = this.savedScenes || []
      const q = this.search.trim().toLowerCase()
      if (!q) return list
      return list.filter((s) => (s.dsc_nome_cena || '').toLowerCase().includes(q))
    },
    canAdd() {
      return !!(this.selected.length && this.idPlaylist && !this.adding)
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.search = ''
        this.selected = []
        this.thumbError = {}
      }
    },
  },
  methods: {
    ...mapActions({ addItemsToPlaylist: 'playout/addItemsToPlaylist' }),
    close() {
      this.$emit('input', false)
    },
    thumbUrl(scene) {
      return getVizrtThumbnailUrl(scene.dsc_vizrt_template_id)
    },
    onThumbError(id) {
      this.$set(this.thumbError, id, true)
    },
    isInPlaylist(scene) {
      return this.existingIds.includes(scene.id_gh_cena)
    },
    isSelected(scene) {
      return this.selected.includes(scene.id_gh_cena)
    },
    toggle(scene) {
      if (this.isInPlaylist(scene)) return
      this.selected = this.isSelected(scene)
        ? this.selected.filter((id) => id !== scene.id_gh_cena)
        : [...this.selected, scene.id_gh_cena]
    },
    async doAdd() {
      if (!this.canAdd) return
      this.adding = true
      try {
        const { items = [], publishErrors = [] } = await this.addItemsToPlaylist({
          idPlaylist: this.idPlaylist,
          idGhCenas: this.selected,
          codProducao: this.codProducao,
        })

        if (publishErrors.length) {
          this.$toast?.error?.(`${items.length} adicionado(s), ${publishErrors.length} não publicado(s) no MSE.`)
        } else {
          this.$toast?.success?.(`${items.length} item(ns) adicionado(s)`)
        }
        this.$emit('added')
        this.selected = []
        this.close()
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.error || 'Erro ao adicionar itens')
      } finally {
        this.adding = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Search field — matches the panel's own search input.
.gg-idrawer__search {
  position: relative;
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 var(--sc-space-3);
  background: var(--sc-gg-surface-2);
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
}

.gg-idrawer__search-input {
  flex: 1 1 auto;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: var(--sc-gg-text-primary);
  font-size: 16px;
  line-height: 24px;

  &::placeholder {
    color: var(--sc-gg-text-tertiary);
  }
}

.gg-idrawer__search-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .v-icon {
    color: var(--sc-gg-text-secondary) !important;
  }
}

.gg-add-item__fallback {
  color: var(--sc-gg-text-tertiary) !important;
}

// Already on the playlist — visible for context, but not addable again.
.gg-add-item--in-playlist {
  cursor: not-allowed;
  opacity: 0.45;
}
</style>
